import { useId } from "react";
import type {
  CustomSankeyLayerProps,
  SankeyLinkDatum,
  SankeyNodeDatum,
} from "@nivo/sankey";
import type { MoneyFlowLink, MoneyFlowNode } from "./useMoneyFlowData";

export const MONEY_FLOW_DIMMED_NODE_OPACITY = 0.2;
const CONNECTED_NODE_OPACITY = 0.55;
// The overlay is drawn over an already dimmed node; account for alpha blending
// so their combined opacity matches the target instead of adding to it.
const CONNECTED_OVERLAY_OPACITY =
  (CONNECTED_NODE_OPACITY - MONEY_FLOW_DIMMED_NODE_OPACITY) /
  (1 - MONEY_FLOW_DIMMED_NODE_OPACITY);

type Node = SankeyNodeDatum<MoneyFlowNode, MoneyFlowLink>;
type Link = SankeyLinkDatum<MoneyFlowNode, MoneyFlowLink>;
type Highlight = { node: Node; y: number; height: number };
type LayerProps = Pick<
  CustomSankeyLayerProps<MoneyFlowNode, MoneyFlowLink>,
  "nodes" | "links" | "currentNode" | "currentLink"
>;

/** Use Nivo's current layout; never infer band positions from transaction totals. */
export function moneyFlowHighlights({
  nodes,
  links,
  currentNode,
  currentLink,
}: Pick<
  LayerProps,
  "nodes" | "links" | "currentNode" | "currentLink"
>): Highlight[] {
  const highlights: Highlight[] = [];
  const fullNode = (node: Node) =>
    highlights.push({ node, y: node.y, height: node.height });
  const slice = (node: Node, link: Link) => {
    const center = link.source.id === node.id ? link.pos0 : link.pos1;
    const y = Math.max(node.y, center - link.thickness / 2);
    const bottom = Math.min(node.y + node.height, center + link.thickness / 2);
    if (bottom > y) highlights.push({ node, y, height: bottom - y });
  };

  if (currentNode) {
    const node = nodes.find((node) => node.id === currentNode.id);
    if (!node) return [];
    fullNode(node);
    for (const link of links) {
      const other =
        link.source.id === node.id
          ? link.target
          : link.target.id === node.id
            ? link.source
            : null;
      if (!other) continue;
      // A group's contribution to a larger total is only a slice. Its own
      // constituent categories still light up in full when hovering the total.
      if (link.drilldownNodeId === node.id) slice(other, link);
      else fullNode(other);
    }
  } else if (currentLink) {
    const link = links.find(
      (link) =>
        link.source.id === currentLink.source.id &&
        link.target.id === currentLink.target.id,
    );
    if (!link) return [];
    const group =
      link.source.id === link.drilldownNodeId ? link.source : link.target;
    const total = group === link.source ? link.target : link.source;
    fullNode(group);
    slice(total, link);
  }
  return highlights;
}

function HighlightSlice({ node, y, height }: Highlight): JSX.Element {
  const clipId = useId();
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect
            x={node.x}
            y={node.y}
            width={node.width}
            height={node.height}
            rx={3}
          />
        </clipPath>
      </defs>
      {(y > node.y || height < node.height) && (
        <rect
          x={node.x}
          y={node.y}
          width={node.width}
          height={node.height}
          fill={node.color}
          fillOpacity={CONNECTED_OVERLAY_OPACITY}
          clipPath={`url(#${clipId})`}
        />
      )}
      <rect
        x={node.x}
        y={y}
        width={node.width}
        height={height}
        fill={node.color}
        clipPath={`url(#${clipId})`}
      />
    </g>
  );
}

/** Native nodes dim uniformly on hover; this overlay restores the relevant parts. */
export function MoneyFlowHighlightLayer(props: LayerProps): JSX.Element {
  return (
    <g pointerEvents="none" aria-hidden="true">
      {moneyFlowHighlights(props).map((highlight, index) => (
        <HighlightSlice key={`${highlight.node.id}-${index}`} {...highlight} />
      ))}
    </g>
  );
}
